import React, { useState, useEffect } from 'react';
import { Noticia, Autor } from '../../types';
import { api } from '../../api/client';
import NoticiaForm from './NoticiaForm';
import { sanitizeHtml } from '../../utils/sanitize';

//const API_BASE = 'https://servicios.algoritmojuridico.com';

export default function NoticiaList() {
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [autores, setAutores] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNoticia, setEditingNoticia] = useState<Noticia | null>(null);

  useEffect(() => {
    Promise.all([api.noticias.list(), api.autores.list()]).then(([noticias, autores]) => {
      setNoticias(noticias);
      const autorMap = autores.reduce((acc, a) => ({ ...acc, [a.id_autor]: a.nombre_autor }), {});
      setAutores(autorMap);
      setLoading(false);
    });
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    await api.noticias.delete(id).then(() => {
      setNoticias(noticias.filter(n => n.id !== id));
    });
  };

  const handleEdit = (n: Noticia) => {
    setEditingNoticia(n);
    setShowForm(true);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingNoticia(null);
    // Recargar
    api.noticias.list().then(setNoticias);
  };

  if (loading) return <p>Cargando noticias...</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Noticias</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          + Nueva Noticia
        </button>
      </div>

      {showForm && (
        <NoticiaForm
          initialData={editingNoticia || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingNoticia(null);
          }}
          isEdit={!!editingNoticia}
          id={editingNoticia?.id}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {noticias.map((n) => (
          <div key={n.id} className="border rounded p-6 bg-white shadow">
            <h2 className="text-xl font-bold" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.titulo || '') }} />
            <p className="text-gray-600 mt-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.resumen || '') }} />
            <div className="mt-3 text-sm text-gray-500">
              <div className="ml-2"><b>Autor: </b>{autores[n.id_autor] || 'Desconocido'}</div>
              <div className="ml-2"><b>Publicado: </b>{new Date(n.fecha_publicacion).toLocaleString()}</div>
              <div className="ml-2"><b>Categoría: </b>{n.categoria}</div>
            </div>
            {n.url_imagen && (
              <img
                src={`${n.url_imagen}`}
                alt={n.titulo}
                className="mt-3 w-full max-w-xs h-auto object-cover rounded"
              />
            )}
            {n.video_url && (
              <div className="mt-3">
                <a
                  href={n.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                  Ver video
                </a>
              </div>
            )}
            <div className="mt-4 space-x-2">
              <button
                onClick={() => handleEdit(n)}
                className="text-blue-600 hover:underline text-sm"
              >
                Editar
              </button>
              <button
                onClick={() => handleDelete(n.id)}
                className="text-red-600 hover:underline text-sm"
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
